import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../api";

export const DEFAULT_USD_NGN_RATE = 1500;

function toNumber(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function getTradeCurrencyCode(trade) {
  return String(trade?.tradeCurrency || "").trim().toUpperCase();
}

export function resolveUsdNgnRate(value) {
  const rate = Number(value);
  return Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_USD_NGN_RATE;
}

export function convertUsdToNgn(value, usdToNgnRate = DEFAULT_USD_NGN_RATE) {
  const amount = toNumber(value) ?? 0;
  return amount * resolveUsdNgnRate(usdToNgnRate);
}

export function convertNgnToUsd(value, usdToNgnRate = DEFAULT_USD_NGN_RATE) {
  const amount = toNumber(value) ?? 0;
  return amount / resolveUsdNgnRate(usdToNgnRate);
}

export function useExchangeRate() {
  const [rateInfo, setRateInfo] = useState({
    pair: "USDNGN",
    rate: DEFAULT_USD_NGN_RATE,
    source: "fallback",
    updatedAt: null,
  });
  const [loadingRate, setLoadingRate] = useState(true);
  const [rateError, setRateError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadRate() {
      setLoadingRate(true);
      setRateError("");

      try {
        const response = await fetch(`${API_BASE_URL}/api/exchange-rate`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || "Failed to load exchange rate.");
        }

        if (active) {
          setRateInfo({
            pair: data?.pair || "USDNGN",
            rate: resolveUsdNgnRate(data?.rate),
            source: data?.source || "live",
            updatedAt: data?.updatedAt || null,
          });
        }
      } catch (error) {
        if (active) {
          setRateError(error.message || "Failed to load exchange rate.");
          setRateInfo((current) => ({
            ...current,
            rate: resolveUsdNgnRate(current.rate),
          }));
        }
      } finally {
        if (active) {
          setLoadingRate(false);
        }
      }
    }

    loadRate();

    return () => {
      active = false;
    };
  }, []);

  return {
    usdToNgnRate: resolveUsdNgnRate(rateInfo.rate),
    rateInfo,
    loadingRate,
    rateError,
  };
}

export function getTradeUsdProfit(trade, usdToNgnRate = DEFAULT_USD_NGN_RATE) {
  const converted = toNumber(trade?.profitUSD);
  if (converted !== null) {
    return converted;
  }

  const raw = toNumber(trade?.profit);
  const currencyCode = getTradeCurrencyCode(trade);
  if (raw !== null) {
    if (currencyCode === "NGN") {
      return convertNgnToUsd(raw, usdToNgnRate);
    }

    return raw;
  }

  const convertedNgn = toNumber(trade?.profitNGN);
  if (convertedNgn !== null) {
    return convertNgnToUsd(convertedNgn, usdToNgnRate);
  }

  return 0;
}

export function getTradeNgnProfit(trade, usdToNgnRate = DEFAULT_USD_NGN_RATE) {
  const convertedUsd = toNumber(trade?.profitUSD);
  if (convertedUsd !== null) {
    return convertUsdToNgn(convertedUsd, usdToNgnRate);
  }

  const raw = toNumber(trade?.profit);
  const currencyCode = getTradeCurrencyCode(trade);
  if (raw !== null) {
    if (currencyCode === "NGN") {
      return raw;
    }

    return convertUsdToNgn(raw, usdToNgnRate);
  }

  const convertedNgn = toNumber(trade?.profitNGN);
  if (convertedNgn !== null) {
    return convertedNgn;
  }

  return 0;
}

export function formatUsd(value) {
  const amount = toNumber(value) ?? 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatNaira(value) {
  const amount = toNumber(value) ?? 0;
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatCompactUsd(value) {
  const amount = toNumber(value) ?? 0;
  const prefix = amount < 0 ? "-" : "";
  return `${prefix}$${new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(Math.abs(amount))}`;
}

export function formatCompactNaira(value) {
  const amount = toNumber(value) ?? 0;
  const prefix = amount < 0 ? "-" : "";
  return `${prefix}₦${new Intl.NumberFormat("en-NG", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(Math.abs(amount))}`;
}

export function DualCurrencyValue({
  usdValue,
  ngnValue,
  usdToNgnRate = DEFAULT_USD_NGN_RATE,
  primaryClassName = "text-current",
  secondaryClassName = "text-xs text-slate-400 dark:text-slate-500",
  compact = false,
}) {
  const primaryAmount = toNumber(usdValue) ?? 0;
  const secondaryAmount = toNumber(ngnValue) ?? convertUsdToNgn(primaryAmount, usdToNgnRate);
  const primary = compact ? formatCompactUsd(primaryAmount) : formatUsd(primaryAmount);
  const secondary = compact ? formatCompactNaira(secondaryAmount) : formatNaira(secondaryAmount);

  return (
    <>
      <div className={primaryClassName}>{primary}</div>
      <div className={`mt-1 ${secondaryClassName}`}>{secondary}</div>
    </>
  );
}